import React from 'react';
import { useRouter } from 'next/router';
import cx from 'classnames';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import * as screenSizes from 'helpers/utils/screensizes';
import { Cell as LayoutElement } from './cell';
import { highlightClassNames, TasticWrapper } from './component';
import { Errors } from './errors';
import { Grid } from './grid';
import {
  Cell as LayoutElementType,
  CellConfiguration,
  Tastic,
  TasticRegistry,
  PageDataResponse,
  PagePreviewDataResponse,
} from './types';

interface MobileDetectionResult {
  isFromMobileApp: boolean;
  error?: string;
}

function isMobileRequest(userAgent: string): MobileDetectionResult {
  try {
    if (!userAgent) {
      return {
        isFromMobileApp: false,
        error: 'User agent string is empty',
      };
    }

    // AAFES MOBILE USER AGENTS TO IDENTIFY IF THE REQUEST IS COMING FORM APP(MEA)
    const mobileAppUserAgents = ['exchange-partner-app', 'AAFESAppMobile'];

    if (mobileAppUserAgents.includes(userAgent)) {
      return {
        isFromMobileApp: true,
      };
    }

    return {
      isFromMobileApp: false,
    };
  } catch (error) {
    return {
      isFromMobileApp: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

export function FrontasticRenderer({
  data,
  tastics = {},
  gridClassName,
  wrapperClassName,
  currentHighlight,
}: {
  data: PageDataResponse & PagePreviewDataResponse & { userAgent: string };
  tastics: TasticRegistry;
  gridClassName?: string;
  wrapperClassName?: string;
  currentHighlight?: string;
}) {
  const [isBiggerThanMobile] = useMediaQuery(screenSizes.mobile);

  const router = useRouter();
  const params = router.query;

  /**
   * =============================================================
   * Controls the visibility of the header & footer .
   *
   * Show header if:
   * - The request is NOT from the mobile app (web browser request).
   * - OR, the 'displayHeaderInApp' / 'displayFooterInApp' flag is explicitly set to true in the request parameters.
   * - OR, the page is NOT in syndicated mode.
   *
   * @param {string} data?.userAgent - User agent string to identify the request source.
   * @param {Object} params - URL or system parameters.
   * @returns {boolean} - Whether to display the header.
   * ==============================================================
   */
  const showHeader =
    !isMobileRequest(data?.userAgent ?? '')?.isFromMobileApp || Boolean(params?.['displayHeaderInApp']);

  const showFooter =
    !isMobileRequest(data?.userAgent ?? '')?.isFromMobileApp || Boolean(params?.['displayFooterInApp']);

  function deviceVisibility(conf: CellConfiguration) {
    return `${conf.mobile ? 'block' : 'hidden'} ${conf.tablet ? 'md:block' : 'md:hidden'} ${
      conf.desktop ? 'lg:block' : 'lg:hidden'
    }`;
  }

  if (data?.page?.sections?.kit) {
    return (
      <Grid wrapperClassName="w-full">
        {data?.page?.sections?.kit?.layoutElements.map((layoutElement: LayoutElementType) => (
          <LayoutElement
            size={layoutElement.configuration.size}
            className={highlightClassNames(currentHighlight === layoutElement.layoutElementId)}
            key={layoutElement.layoutElementId}
          >
            {layoutElement.tastics.map((t) => (
              <TasticWrapper
                tastics={tastics}
                key={t.tasticId}
                data={t}
                dataSources={data.data.dataSources}
                pageFolder={data.pageFolder}
                highlight={currentHighlight === t.tasticId}
                previewId={data?.previewId}
              />
            ))}
          </LayoutElement>
        ))}
      </Grid>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-stretch justify-start">
      {process && process.env.NODE_ENV !== 'production' && <Errors />}
      {showHeader && (
        <Grid
          gridClassName={gridClassName}
          wrapperClassName={`${wrapperClassName} w-full headercontainer ${highlightClassNames(
            currentHighlight === 'head',
          )}`}
        >
          {data?.page?.sections?.head?.layoutElements.map((layoutElement: LayoutElementType) => (
            <LayoutElement
              size={layoutElement.configuration.size}
              className={cx(
                highlightClassNames(currentHighlight === layoutElement.layoutElementId),
                deviceVisibility(layoutElement.configuration),
              )}
              key={layoutElement.layoutElementId}
            >
              {layoutElement.tastics.map((t) => (
                <TasticWrapper
                  tastics={tastics}
                  key={t.tasticId}
                  data={t}
                  dataSources={data.data.dataSources}
                  pageFolder={data.pageFolder}
                  highlight={currentHighlight === t.tasticId}
                  previewId={data?.previewId}
                />
              ))}
            </LayoutElement>
          ))}
        </Grid>
      )}
      <Grid
        gridClassName={`$gridClassName={gridClassName}`}
        wrapperClassName={`${wrapperClassName} w-full grow ${highlightClassNames(currentHighlight === 'main')}`}
      >
        {data?.page?.sections?.main?.layoutElements.map((layoutElement: LayoutElementType) => {
          return (
            <LayoutElement
              size={isBiggerThanMobile ? layoutElement.configuration.size : 12}
              className={cx(
                highlightClassNames(currentHighlight === layoutElement.layoutElementId),
                deviceVisibility(layoutElement.configuration),
              )}
              key={layoutElement.layoutElementId}
            >
              {layoutElement.tastics.map((t: Tastic) => (
                <TasticWrapper
                  tastics={tastics}
                  key={t.tasticId}
                  data={t}
                  dataSources={data.data.dataSources}
                  pageFolder={data.pageFolder}
                  highlight={currentHighlight === t.tasticId}
                  previewId={data?.previewId}
                />
              ))}
            </LayoutElement>
          );
        })}
      </Grid>
      {showFooter && (
        <Grid
          gridClassName={gridClassName}
          wrapperClassName={`px-0 lg:max-w-[1440px] lg:m-auto w-full  ${highlightClassNames(
            currentHighlight === 'footer',
          )}`}
        >
          {data?.page?.sections?.footer?.layoutElements.map((layoutElement: LayoutElementType) => (
            <LayoutElement
              size={layoutElement.configuration.size}
              className={cx(
                highlightClassNames(currentHighlight === layoutElement.layoutElementId),
                deviceVisibility(layoutElement.configuration),
              )}
              key={layoutElement.layoutElementId}
            >
              {layoutElement.tastics.map((t: Tastic) => (
                <TasticWrapper
                  tastics={tastics}
                  key={t.tasticId}
                  data={t}
                  dataSources={data.data.dataSources}
                  pageFolder={data.pageFolder}
                  highlight={currentHighlight === t.tasticId}
                  previewId={data?.previewId}
                />
              ))}
            </LayoutElement>
          ))}
        </Grid>
      )}
    </div>
  );
}
